import { useCallback } from "react";
import { gql } from "@apollo/client";
import { toast } from "sonner";
import { useUpdateMyOrgMutation } from "./useSwitchOrgAndReload.generated";
import { useRouter } from "next/router";

type Return = {
  switchOrgAndReload: (orgId: number, orgName: string) => Promise<boolean>;
  syncOrgIdToUrl: (orgId: number, orgName: string) => void;
};

export const ORG_SWITCH_CHANNEL = "org-switch";
export const ORG_SWITCH_ATTEMPTED_KEY = "orgSwitchAttempted";
export const TAB_ID = Math.random().toString(36).slice(2);

const useSwitchOrgAndReload = (): Return => {
  const [updateMyOrg] = useUpdateMyOrgMutation();
  const router = useRouter();
  const syncOrgIdToUrl = useCallback(
    async (orgId: number, orgName: string): Promise<void> => {
      const expectedOrgValue = formatOrgIdAndNameForUrl(orgId, orgName);
      await router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            org: expectedOrgValue,
          },
        },
        undefined,
        { shallow: true },
      );
    },
    [router],
  );
  const switchOrgAndReload = useCallback(
    async (orgId: number, orgName: string) => {
      const result = await updateMyOrg({ variables: { orgId } });
      if (!result.data?.updateMyOrg?.success) {
        toast.error(`Couldn't switch to ${orgName}`);
        return false;
      }
      sessionStorage.setItem(ORG_SWITCH_ATTEMPTED_KEY, String(orgId));
      const channel = new BroadcastChannel(ORG_SWITCH_CHANNEL);
      channel.postMessage({ orgId, orgName, tabId: TAB_ID });
      channel.close();
      await syncOrgIdToUrl(orgId, orgName);
      router.reload();
      return true;
    },
    [updateMyOrg, router, syncOrgIdToUrl],
  );
  return { switchOrgAndReload, syncOrgIdToUrl };
};
export default useSwitchOrgAndReload;

export const formatOrgIdAndNameForUrl = (
  orgId: number,
  orgName: string,
): string => {
  return `${orgId}-${orgName}`;
};

export const getOrgIdAndNameFromUrl = (): [
  number | undefined,
  string | undefined,
] => {
  const urlParams = new URLSearchParams(window.location.search);
  const urlOrg = urlParams.get("org");
  if (!urlOrg) return [undefined, undefined];
  return [
    parseInt(urlOrg.split("-")[0], 10),
    urlOrg.split("-").slice(1).join("-"),
  ];
};

gql`
  mutation UpdateMyOrg($orgId: Int!) {
    updateMyOrg(orgId: $orgId) {
      success
    }
  }
`;
